/**
 * navigation/platformAuthRedirect.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Company Cash does NOT own authentication. The SK Platform owns the only
 * login / signup / forgot-password screens for every product. When this console
 * has no valid session it bounces the user to the centralized platform auth,
 * carrying the product handoff:
 *
 *     ?product=company_cash&return_to=<where they were>&state=<nonce>
 *
 * plus a short-lived `sk_auth_state` cookie that the platform verifies
 * (CSRF / first-party-initiation — see skplatform-fe
 * src/navigation/productHandoff.ts `verifyState`).
 *
 * ALL THREE PARAMS ARE REQUIRED. The platform's `readHandoffFromUrl()` returns
 * null unless `product` AND `state` are both present, and `resolveReturnTarget`
 * refuses to redirect for an unknown product. Sending only `return_to` — which
 * is what this console did before — signs the user in successfully and then
 * strands them on the platform dashboard, because the handoff was never
 * captured. It looks like the console is broken; nothing in a unit test can see
 * it.
 *
 * `return_to` is additionally checked server-side against the product registry's
 * allow-list (GET /v1/products/redirect-targets, fed by
 * `APP_ORIGIN_COMPANY_CASH`), so an attacker-supplied origin cannot be used as
 * an open redirect.
 *
 * Web-only; every export is a no-op on native, where the platform app owns the
 * hand-off and delivers the token over the deep link instead.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Platform } from 'react-native';
import { PLATFORM_APP_ORIGIN } from '../api/endpoints';

/** The registry slug. Must match `JWT_AUDIENCE` on sk-company-cash-api. */
const PRODUCT = 'company_cash';
const STATE_COOKIE = 'sk_auth_state';

/**
 * The platform auth app.
 *
 * `APP_ORIGIN_PLATFORM` (DefinePlugin, see web/webpack.config.js) wins when it
 * is set, so a deployment can point the console at a specific platform. The
 * hostname fallback mirrors the other SK products, and means a build with no
 * build-arg still lands somewhere correct rather than on localhost in
 * production.
 */
function resolvePlatformAuthUrl(): string {
  if (PLATFORM_APP_ORIGIN) return `${PLATFORM_APP_ORIGIN.replace(/\/$/, '')}/`;
  if (typeof window === 'undefined') return 'https://platform.softknack.com/';
  const host = window.location.hostname;
  if (/^(localhost|127\.0\.0\.1)$/.test(host)) return 'http://localhost:3009/';
  if (host.includes('.dev.')) return 'https://platform.dev.softknack.com/';
  if (host.includes('.staging.')) return 'https://platform.staging.softknack.com/';
  return 'https://platform.softknack.com/';
}

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

/** 128 bits of hex. Single-use, and only ever compared against our own cookie. */
function randomState(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Write the state cookie the platform will verify.
 *
 * Parent-domain scope on real hosts (`.softknack.com`) so the platform on
 * another subdomain can read it; host-only on localhost, where `domain=` is not
 * usable — cookies ignore the PORT, so `localhost:3017` and `localhost:3009`
 * still share it.
 */
function setStateCookie(state: string): void {
  const parts = window.location.hostname.split('.');
  const domain =
    parts.length >= 2 && parts[parts.length - 1] !== 'localhost'
      ? `; domain=.${parts.slice(-2).join('.')}`
      : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATE_COOKIE}=${state}; Max-Age=600; path=/; SameSite=Lax${domain}${secure}`;
}

/**
 * Guard against firing twice in one page load. The boot probe and a parallel
 * 401 can both decide to redirect; the first one navigates away, and a second
 * `assign()` would replace the URL — and the state cookie — mid-flight.
 */
let redirecting = false;

/** Bounce to the centralized platform auth, returning here after sign-in. */
export function redirectToPlatformAuth(): void {
  if (!isWeb() || redirecting) return;
  redirecting = true;

  const state = randomState();
  setStateCookie(state);

  const url = new URL(resolvePlatformAuthUrl());
  url.searchParams.set('product', PRODUCT);
  url.searchParams.set('return_to', window.location.href);
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

/**
 * Hand a verification or reset token back to the platform, which owns the ONLY
 * verify / reset / "link expired" screens for every product. A Company Cash
 * email link that lands here is re-processed there, so an expired link shows the
 * platform's own screen rather than a local imitation of it.
 */
function redirectToPlatformMode(mode: 'verify' | 'reset', token: string): void {
  if (!isWeb() || redirecting) return;
  redirecting = true;

  const url = new URL(resolvePlatformAuthUrl());
  url.searchParams.set('mode', mode);
  url.searchParams.set('token', token);
  window.location.assign(url.toString());
}

export const redirectToPlatformVerify = (token: string): void =>
  redirectToPlatformMode('verify', token);

export const redirectToPlatformReset = (token: string): void =>
  redirectToPlatformMode('reset', token);

/**
 * Read (and clear) a `?mode=verify|reset&token=…` from the URL.
 *
 * Cleared on read so a reload or a return trip does not re-process a token the
 * platform has already consumed — which would show the user an "invalid link"
 * screen for a link that worked.
 */
export function takeAuthToken(): { mode: 'verify' | 'reset'; token: string } | null {
  if (!isWeb()) return null;
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  if (mode !== 'verify' && mode !== 'reset') return null;
  const token = params.get('token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname);
  return { mode, token };
}
